'use client';

import { MotionMaybe, useMotionControls } from '@/utils/animation';
import { getColorScheme } from '@/utils/getColorScheme';
import { getCTA } from '@/utils/getCTA';
import { getVerticalSpacing } from '@/utils/getVerticalSpacing';
import IconValue from './atoms/IconValue';
import { MarkDown } from './atoms/MarkDown';

export default function Financial(props) {
    const {
        Anchor,
        Icon,
        Header,
        Body,
        Image,
        AltText = '',
        ImageLink = '',
        ImageVariant = 'cover',
        CTA,
        CustomCTALink,
        SecondaryButtonText,
        SecondaryButtonLink,
        OutlineButtonText,
        OutlineButtonLink,
        ColorScheme = 'secondary',
        VerticalSpacing = 'medium',
        Animation = 'fade',
        Duration: DurationRaw = 0.6,
        EditorPreview = false,
        AnimationGroup = 'content',
    } = props;

    const {
        backgroundColor,
        textColor,
        iconColor,
        headerColor,
        secondaryButton,
        thirdButton,
        backgroundStyle,
    } = getColorScheme(ColorScheme, props.GradientDirection);
    const vSpace = getVerticalSpacing(VerticalSpacing);
    const Duration = Number(DurationRaw) || 0.6;

    const rawGroup = String(AnimationGroup || 'content').toLowerCase();
    const groupNormalized = rawGroup === 'media' ? 'medias' : rawGroup;
    const animationGroup = ['content', 'medias', 'ctas'].includes(
        groupNormalized
    )
        ? groupNormalized
        : 'content';

    const m = useMotionControls({
        preset: Animation,
        duration: Duration,
        editorPreview: EditorPreview,
        group: animationGroup,
        keyBase: 'financial',
    });

    if (!Header || !Body) return <div>failed to load</div>;

    // image classing (unchanged)
    const v = String(ImageVariant).toLowerCase();
    const isSquare = v.includes('square');
    const isCover = v.includes('cover');
    const isContain = v.includes('contain');
    const isSmall = v.includes('small');
    const noShadow = v.includes('no-shadow');

    let shape = isSquare
        ? 'rounded-[var(--shape-radius)]'
        : 'rounded-[min(1.5rem,var(--shape-radius))]';
    let shadow = noShadow ? '' : 'media-soft-shadow';
    let imageClass;
    if (isSmall) imageClass = 'object-contain w-auto h-auto max-w-96 max-h-48';
    else if (isContain)
        imageClass = 'object-contain w-auto h-auto max-w-96 max-h-96';
    else if (isSquare)
        imageClass =
            'object-cover mx-auto w-[300px] h-[300px] md:w-[500px] md:h-[500px]';
    else if (isCover)
        imageClass = 'object-cover w-full h-[300px] md:h-[400px] lg:h-[500px]';
    else {
        imageClass = 'w-auto h-auto';
        shape = '';
        shadow = '';
    }

    const { element: CTAs, hasAny } = getCTA(
        {
            ...props,
            CTA: String(CTA ?? '').trim(),
            CustomCTALink: String(CustomCTALink ?? '').trim(),
            SecondaryButtonText: String(SecondaryButtonText ?? '').trim(),
            SecondaryButtonLink: String(SecondaryButtonLink ?? '').trim(),
            OutlineButtonText: String(OutlineButtonText ?? '').trim(),
            OutlineButtonLink: String(OutlineButtonLink ?? '').trim(),
        },
        {
            secondaryVariant: secondaryButton,
            outlineVariant: thirdButton,
            buttonAlign: 'justify-center',
            enableAnim: m.enableChild('ctas'),
            motionProps: m.childMotionProps,
        }
    );

    return (
        <section
            id={Anchor}
            className={`editable-component relative overflow-hidden ${backgroundColor} px-6 ${vSpace} lg:px-8`}
            style={backgroundStyle}
            data-anim={Animation}
            data-dur={Duration}
        >
            <div className="relative z-40 mx-auto max-w-2xl lg:max-w-4xl">
                {/* CONTENT */}
                <MotionMaybe
                    enable={m.enableContainer('content')}
                    nodeKey={m.remountKey + '---content'}
                    motionProps={m.containerMotionProps}
                    className="text-center"
                >
                    {Icon && (
                        <MotionMaybe
                            enable={m.enableChild('content')}
                            nodeKey={m.remountKey + '---icon'}
                            motionProps={m.childMotionProps}
                        >
                            <IconValue
                                value={Icon}
                                className={`mx-1 mb-6 text-4xl ${iconColor} sm:text-4xl lg:text-6xl`}
                            />
                        </MotionMaybe>
                    )}

                    <MotionMaybe
                        enable={m.enableChild('content')}
                        nodeKey={m.remountKey + '---header'}
                        motionProps={m.childMotionProps}
                    >
                        <MarkDown
                            variant="h2"
                            className={`font-display text-2xl tracking-tight ${headerColor} md:text-3xl lg:text-4xl`}
                        >
                            {String(Header ?? '')}
                        </MarkDown>
                    </MotionMaybe>

                    <MotionMaybe
                        enable={m.enableChild('content')}
                        nodeKey={m.remountKey + '---body'}
                        motionProps={m.childMotionProps}
                    >
                        <MarkDown
                            className={`mt-10 text-base font-text leading-8 md:text-lg ${textColor} sm:leading-9`}
                        >
                            {String(Body ?? '')}
                        </MarkDown>
                    </MotionMaybe>
                </MotionMaybe>

                {/* MEDIA */}
                {Image && (
                    <MotionMaybe
                        enable={m.enableContainer('medias')}
                        nodeKey={m.remountKey + '---media'}
                        motionProps={m.containerMotionProps}
                        className="mt-10 flex justify-center"
                    >
                        <MotionMaybe
                            enable={m.enableChild('medias')}
                            nodeKey={m.remountKey + '---media-child'}
                            motionProps={m.childMotionProps}
                        >
                            {ImageLink ? (
                                <a href={ImageLink}>
                                    <img
                                        src={Image}
                                        alt={AltText}
                                        className={`${imageClass} ${shape} ${shadow}`}
                                        style={{
                                            willChange: 'opacity, transform',
                                        }}
                                    />
                                </a>
                            ) : (
                                <img
                                    src={Image}
                                    alt={AltText}
                                    className={`${imageClass} ${shape} ${shadow}`}
                                    style={{ willChange: 'opacity, transform' }}
                                />
                            )}
                        </MotionMaybe>
                    </MotionMaybe>
                )}

                {/* CTAS */}
                {hasAny && (
                    <MotionMaybe
                        enable={m.enableContainer('ctas')}
                        nodeKey={m.remountKey + '---ctas'}
                        motionProps={m.containerMotionProps}
                        className="mt-10 flex items-center justify-center lg:mt-16"
                    >
                        {CTAs}
                    </MotionMaybe>
                )}
            </div>
        </section>
    );
}
